import { Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import downloadFill from "@iconify/icons-eva/download-fill";
import { isValid } from "date-fns";
import {
  EXPORT_STATUS,
  STATUS_COLOR,
  STATUS_LABEL,
} from "./invoiceExport.constants";
import {
  formatAccessWindow,
  formatPartLabel,
  formatPeriod,
  isAccessExpired,
  parseExportParts,
} from "./invoiceExport.utils";
import { dateTimeShortFormatInTimezone } from "src/utils/timezoneFormat";

const formatRequestedAt = (value, timeZone) => {
  if (!value) return "-";
  return isValid(new Date(value))
    ? dateTimeShortFormatInTimezone(value, timeZone)
    : "-";
};

const UnavailableText = () => (
  <Typography variant="caption" color="text.secondary">
    No longer available
  </Typography>
);

const PartDownloadList = ({ parts }) => {
  // Parts of one export share a retention window, so the availability caption
  // is shown once for the group rather than repeated on every row. Taking it
  // from the earliest-expiring live part keeps it honest if they ever diverge.
  const accessWindow = parts
    .filter((part) => !isAccessExpired(part))
    .map((part) => formatAccessWindow(part))
    .filter(Boolean)[0];

  return (
    <Stack spacing={0.5} alignItems="flex-start" sx={{ py: 1, width: "100%" }}>
      {parts.map((part, index) => {
        const expired = isAccessExpired(part);
        return (
          <Stack
            key={part?.blobPath ?? part?.part ?? index}
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <Button
              size="small"
              variant="outlined"
              component="a"
              href={part.downloadUrl}
              // The SAS link points at Blob Storage, so let the browser handle
              // the transfer instead of buffering the zip in JS.
              download
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Icon icon={downloadFill} />}
              disabled={expired || !part.downloadUrl}
            >
              {formatPartLabel(part, index)}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {expired
                ? "No longer available"
                : `${part.invoiceCount ?? 0} invoices`}
            </Typography>
          </Stack>
        );
      })}
      {accessWindow && (
        <Typography variant="caption" color="text.secondary">
          {accessWindow}
        </Typography>
      )}
    </Stack>
  );
};

const SingleDownload = ({ row }) => {
  const accessWindow = formatAccessWindow(row);
  return (
    <Stack spacing={0.25} alignItems="flex-start">
      <Button
        size="small"
        variant="outlined"
        component="a"
        href={row.downloadUrl}
        download
        target="_blank"
        rel="noopener noreferrer"
        startIcon={<Icon icon={downloadFill} />}
      >
        Download
      </Button>
      {accessWindow && (
        <Typography variant="caption" color="text.secondary">
          {accessWindow}
        </Typography>
      )}
    </Stack>
  );
};

const ExportActionsCell = ({ row }) => {
  const parts = parseExportParts(row.parts);

  if (row.status === EXPORT_STATUS.READY && parts.length) {
    // Only collapse to a single message when every part has lapsed.
    if (parts.every((part) => isAccessExpired(part))) return <UnavailableText />;
    return <PartDownloadList parts={parts} />;
  }

  if (row.status === EXPORT_STATUS.READY && row.downloadUrl) {
    if (isAccessExpired(row)) return <UnavailableText />;
    return <SingleDownload row={row} />;
  }

  if (row.status === EXPORT_STATUS.FAILED) {
    return (
      <Tooltip title={row.errorReason || "Something went wrong"}>
        <Typography variant="caption" color="error">
          Failed - contact support
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Typography variant="caption" color="text.secondary">
      Preparing...
    </Typography>
  );
};

export const invoiceExportColumns = (timeZone) => [
  {
    field: "period",
    headerName: "Period",
    flex: 1.4,
    minWidth: 220,
    sortable: false,
    valueGetter: ({ row }) => formatPeriod(row.periodFrom, row.periodTo),
  },
  {
    field: "requestedAt",
    headerName: "Requested on",
    flex: 1,
    minWidth: 170,
    valueGetter: ({ row }) => formatRequestedAt(row.requestedAt, timeZone),
  },
  {
    field: "invoiceCount",
    headerName: "Invoices",
    flex: 0.6,
    minWidth: 100,
    // Only known once the job has actually run.
    valueGetter: ({ row }) => row.invoiceCount ?? "-",
  },
  {
    field: "status",
    headerName: "Status",
    flex: 0.8,
    minWidth: 130,
    renderCell: ({ row }) => (
      <Chip
        size="small"
        label={STATUS_LABEL[row.status] ?? row.status}
        color={STATUS_COLOR[row.status] ?? "default"}
        variant={row.status === EXPORT_STATUS.READY ? "filled" : "outlined"}
      />
    ),
  },
  {
    field: "actions",
    headerName: "Actions",
    flex: 1,
    // Wide enough for the per-part download buttons and their captions.
    minWidth: 200,
    sortable: false,
    renderCell: ({ row }) => <ExportActionsCell row={row} />,
  },
];
